import React, { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { Lightbulb } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import OnboardingSuccess from './components/OnboardingSuccess';
import OnboardingCanceled from './components/OnboardingCanceled';
import EmailVerification from './components/EmailVerification';
import PasswordReset from './components/PasswordReset';
import { CompanyProvider } from './contexts/CompanyContext';
import { LoadingOverlay } from './components/LoadingOverlay';
import { LoadingProvider } from './contexts/LoadingContext';
import { TrialProvider, useTrial } from './contexts/TrialContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import TrialExpiredModal from './components/TrialExpiredModal';
import EmailVerifyBanner from './components/EmailVerifyBanner';
import { useCompany } from './contexts/CompanyContext';
import ModernUpdateToast from './components/ModernUpdateToast';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PolicyUpdateBanner from './components/PolicyUpdateBanner';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import { useVersionCheck } from './hooks/useVersionCheck';
import { ConsultorProvider } from './contexts/ConsultorContext';
import { WhatsAppTabsProvider } from './contexts/WhatsAppTabsContext';
import { ChatProvider, useChatContext } from './contexts/ChatContext';
import NuvemshopCallback from './components/NuvemshopCallback';
import NuvemshopInstall from './components/NuvemshopInstall';
import { temInstalacaoPendente } from './utils/nuvemshopInstalacao';
import { useAuth } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MuiThemeBridge } from './styles/MuiThemeBridge';
import './App.css';
import './styles/globals.css';
import './config/axios';
import { logger } from './utils/logger';

/** Banners globais do app (política, trial, e-mail, instalar PWA).
 *  Nas páginas públicas de certificado (/k/...) nada disso deve aparecer:
 *  quem acessa é o cliente final da marca, não um usuário do app IAT. */
const AppBanners = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/k/')) return null;
  return (
    <>
      <PolicyUpdateBanner />
      <TrialBillingGate />
      <EmailVerifyBanner />
      <PwaInstallPrompt />
    </>
  );
};

/** Bloqueia rotas que exigem login: sem usuário autenticado, manda pro /login
 *  em vez de tentar renderizar a tela (que fica presa esperando dados que
 *  nunca chegam pra quem não está logado — foi assim que o revisor do Google
 *  viu o app "não abrir" e rejeitou a publicação). */
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LazyLoadingFallback />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
};

/** Retoma a instalação vinda da Nuvemshop assim que o lojista entra ou se cadastra.
 *  O lojista que instalou pela Loja de Aplicativos sem ter conta é mandado para o
 *  cadastro; sem isto ele terminaria o cadastro dentro do app e a loja nunca seria
 *  conectada — ele teria que descobrir sozinho o caminho de volta. */
const NuvemshopInstallResume = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if ((location.pathname || '').startsWith('/integracoes/nuvemshop')) return;
    if (!temInstalacaoPendente()) return;
    navigate('/integracoes/nuvemshop/instalar', { replace: true });
  }, [user, location.pathname, navigate]);

  return null;
};

// Título da aba por rota (precisa estar dentro de <Router> p/ useLocation funcionar)
const RouteTitle = () => {
  const location = useLocation();
  const { unreadTotal } = useChatContext();
  useEffect(() => {
    const path = location.pathname || '';
    let base = 'App · IAT';
    if (path.startsWith('/settings')) base = 'Config · IAT';
    else if (path.startsWith('/chat')) base = 'Painel · IAT';
    else if (path.startsWith('/k/')) base = 'Certificado de Autenticidade';
    else if (path.startsWith('/certificados')) base = 'Certificados · IAT';
    document.title = unreadTotal > 0 ? `(${unreadTotal}) ${base}` : base;
  }, [location.pathname, unreadTotal]);
  return null;
};

// ✅ FASE 4: Lazy loading de componentes pesados para reduzir bundle inicial
const ChatInterface = lazy(() => import('./components/ChatInterface'));
const ChatSettings = lazy(() => import('./components/ChatSettings'));
const CertificatePublic = lazy(() => import('./components/CertificatePublic'));
const CertificatesAdmin = lazy(() => import('./components/CertificatesAdmin'));
const CrmScreen = lazy(() => import('./components/crm/CrmScreen'));

// ✅ FASE 4: Componente de loading para lazy loaded components - lâmpada amarela piscando
const LazyLoadingFallback = () => (
  <div className="flex items-center justify-center h-screen" style={{ background: 'var(--bg-base)' }}>
    <div className="animate-pulse">
      <Lightbulb size={32} className="text-amber-400 blinking-light" />
    </div>
    <style>
      {`
      @keyframes blink {
        0% { opacity: 0.2; }
        50% { opacity: 1; }
        100% { opacity: 0.2; }
      }
      .blinking-light {
        animation: blink 1.5s infinite;
      }
      `}
    </style>
  </div>
);

const BILLING_DISMISS_KEY = (companyId) => `iat_billing_dismiss_${companyId}`;

/** Trial expirado ou checkout pendente: bloqueia /chat sempre; em /settings permite após ✕ (modo limitado). */
const TrialBillingGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isTrialExpired, trialData, isLoading } = useTrial();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.companyId;

  const [settingsDismissed, setSettingsDismissed] = useState(false);

  useEffect(() => {
    if (!companyId) {
      setSettingsDismissed(false);
      return;
    }
    try {
      setSettingsDismissed(localStorage.getItem(BILLING_DISMISS_KEY(companyId)) === '1');
    } catch (_) {
      setSettingsDismissed(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId || isLoading) return;
    if (!isTrialExpired) {
      try {
        localStorage.removeItem(BILLING_DISMISS_KEY(companyId));
      } catch (_) { /* ignore */ }
      setSettingsDismissed(false);
    }
  }, [isTrialExpired, companyId, isLoading]);

  const handleLimitedSettingsAccess = useCallback(async () => {
    if (!companyId) return;
    try {
      localStorage.setItem(BILLING_DISMISS_KEY(companyId), '1');
    } catch (_) { /* ignore */ }
    setSettingsDismissed(true);
    navigate(`/settings?companyId=${encodeURIComponent(companyId)}`, { replace: true });
  }, [companyId, navigate]);

  const isAppRoute =
    location.pathname === '/chat' || location.pathname === '/settings';

  if (!isAppRoute || isLoading || !companyId) return null;

  if (!isTrialExpired || !trialData) return null;

  const role = (selectedCompany?.role || selectedCompany?.user?.role || 'atendente').toLowerCase();
  const isAdmin = role === 'admin';

  const showModal =
    location.pathname === '/chat' || (location.pathname === '/settings' && !settingsDismissed);

  return (
    <TrialExpiredModal
      isOpen={showModal}
      userEmail={trialData.userEmail}
      subscriptionPlan={trialData.subscriptionPlan}
      type={trialData.type || 'trial'}
      isAdmin={isAdmin}
      selectedCompany={selectedCompany}
      onLimitedSettingsAccess={handleLimitedSettingsAccess}
    />
  );
};

const AppContent = () => {
  // ✅ REATIVADO: Sistema de notificação de atualização
  // Importante para garantir que usuários atualizem rapidamente após otimizações críticas
  // Reduz custos do Firestore ao garantir que todos usem código otimizado
  const { hasUpdate, serverVersion, reloadPage } = useVersionCheck();
  
  // ✅ DEBUG: Expor no window para teste manual (apenas em desenvolvimento)
  if (process.env.NODE_ENV === 'development') {
    window.__TEST_UPDATE_NOTIFICATION__ = () => {
      logger.log('[TEST] Forçando notificação de atualização...');
      // Isso não vai funcionar diretamente, mas vamos adicionar uma forma melhor
    };
  }

  return (
    <Router>
      <RouteTitle />
      <NuvemshopInstallResume />
      <AppBanners />
      <Suspense fallback={<LazyLoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/register" element={<RegisterScreen />} />
          <Route path="/verificar-email" element={<EmailVerification />} />
          <Route path="/redefinir-senha" element={<PasswordReset />} />
          <Route path="/onboarding/success" element={<OnboardingSuccess />} />
          <Route path="/onboarding/canceled" element={<OnboardingCanceled />} />
          {/* Retorno do OAuth da Nuvemshop (app de parceiro): a URL cadastrada
              no Portal de Parceiros aponta para cá. */}
          <Route path="/integracoes/nuvemshop/callback" element={<NuvemshopCallback />} />
          {/* Boas-vindas da instalação vinda da Loja de Aplicativos: quem ainda não
              tem conta escolhe entre entrar e se cadastrar; quem já está logado é
              levado direto para a autorização. */}
          <Route path="/integracoes/nuvemshop/instalar" element={<NuvemshopInstall />} />
          {/* Certificado de autenticidade (NFC/QR) — página pública, sem login */}
          <Route path="/k/:slug/:code" element={
            <Suspense fallback={<LazyLoadingFallback />}>
              <CertificatePublic />
            </Suspense>
          } />
          {/* Admin de certificados (empresas com a feature habilitada) */}
          <Route path="/certificados" element={
            <RequireAuth>
              <Suspense fallback={<LazyLoadingFallback />}>
                <CertificatesAdmin />
              </Suspense>
            </RequireAuth>
          } />
          <Route path="/chat" element={
            <RequireAuth>
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <ConsultorProvider>
                    <ChatInterface />
                  </ConsultorProvider>
                </Suspense>
              </ErrorBoundary>
            </RequireAuth>
          }>
            {/* CRM como rota FILHA: o atendimento continua montado por trás,
                então voltar é instantâneo e a conversa aberta não se perde.
                Endereço próprio garante o botão voltar do Android (TWA), o F5
                e os links que a Beny manda. */}
            <Route path="crm" element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <CrmScreen />
              </Suspense>
            } />
            <Route path="crm/:secao" element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <CrmScreen />
              </Suspense>
            } />
          </Route>
          <Route path="/settings" element={
            <RequireAuth>
              <Suspense fallback={<LazyLoadingFallback />}>
                <ConsultorProvider>
                  <ChatSettings />
                </ConsultorProvider>
              </Suspense>
            </RequireAuth>
          } />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
      <LoadingOverlay />

      {/* Toasts de feedback (salvo/enviado/erro) — top-center, fora da safe-area */}
      <ToastContainer
        position="top-center"
        autoClose={2600}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        theme="dark"
        style={{ marginTop: 'var(--safe-area-top)' }}
      />

      {/* ✅ REATIVADO: Notificação de atualização disponível */}
      <ModernUpdateToast
        hasUpdate={hasUpdate}
        serverVersion={serverVersion}
        onReload={reloadPage}
      />

    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <CompanyProvider>
          {/* ✅ FASE 2: Notificações Centralizadas - 1 único listener para toda a aplicação */}
          <NotificationProvider>
            {/* ✅ FASE 3: Abas Centralizadas */}
            <WhatsAppTabsProvider>
              <TrialProvider>
                <PaymentProvider>
                  {/* ✅ ETAPA 0.3: ChatProvider ativado - estado global do chat disponível para toda a árvore */}
                  <ChatProvider>
                    {/* ✅ ETAPA 1: UIProvider centraliza todos os estados de modal/UI */}
                    <UIProvider>
                      <ThemeProvider>
                        <MuiThemeBridge>
                          <AppContent />
                        </MuiThemeBridge>
                      </ThemeProvider>
                    </UIProvider>
                  </ChatProvider>
                </PaymentProvider>
              </TrialProvider>
            </WhatsAppTabsProvider>
          </NotificationProvider>
        </CompanyProvider>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
